'use client'
import { memo, useState } from 'react'
import type { FC, ChangeEvent, KeyboardEvent, FocusEvent } from 'react'
import styled from 'styled-components'
import { mq, vw } from '@/styles'
import { useCart } from '@/context/project/CartContext'
import { canAddToCart } from '@/utils'
import { E_DELIVERY_TYPE } from '@/schema'
import type { TypeCmsVariant } from '@/schema'
import { motion } from 'motion/react'

type Props = {
  variant: TypeCmsVariant
  quantity: number
  frozen?: boolean
  horizontal?: boolean
  addPerBox?: boolean
  isLoading?: boolean
  displayOnlyRemainingUnits?: boolean
}

export const QuantityInput: FC<Props> = memo(({ variant, quantity, frozen = false, horizontal = false, addPerBox = false, isLoading = false, displayOnlyRemainingUnits = false }) => {
  const { updateCartQuantity, cartLines } = useCart()
  const qtyPerBox = variant.qtyPerBox || 1
  // console.log('Rendering QuantityInput for variant:', variant.id, 'with quantity:', quantity)

  // Calculate display value based on mode
  const getDisplayValue = (qty: number) => {
    if (displayOnlyRemainingUnits) return (qty % qtyPerBox).toString()
    if (addPerBox) return Math.floor(qty / qtyPerBox).toString()
    return qty.toString()
  }

  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')

  // ✅ Derive the display value during rendering (no Effect needed)
  const inputValue = isEditing ? editValue : getDisplayValue(quantity)

  const maxQuantity = (() => {
    const stock = variant.stock || 0
    const product = variant.product
    const inventoryTracking = product?.inventoryTracking

    if (
      variant.deliveryType === E_DELIVERY_TYPE.ON_DEMAND &&
      inventoryTracking === false
    ) {
      return Infinity
    }

    return stock
  })()

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value

    if (/^\d*$/.test(value)) {
      setEditValue(value)
    }
  }

  const handleInputFocus = (event: FocusEvent<HTMLInputElement>) => {
    setEditValue(getDisplayValue(quantity))
    setIsEditing(true)
    event.target.select()
  }

  const handleInputBlur = () => {
    updateQuantity()
    setTimeout(() => setIsEditing(false), 10)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.currentTarget.blur()
    }
  }

  const updateQuantity = () => {
    const inputQuantity = parseInt(inputValue) || 0
    let finalQuantity: number

    if (addPerBox) {
      // Convert boxes to units
      finalQuantity = inputQuantity * qtyPerBox
    } else if (displayOnlyRemainingUnits) {
      // Keep the current boxes and update only the remaining units
      const currentBoxes = Math.floor(quantity / qtyPerBox)
      finalQuantity = (currentBoxes * qtyPerBox) + inputQuantity
    } else {
      // Regular unit mode
      const minSaleUnits = variant.minSaleUnits || 1

      if (inputQuantity === 0) {
        finalQuantity = 0
      } else if (inputQuantity < minSaleUnits) {
        finalQuantity = minSaleUnits
      } else {
        finalQuantity = Math.ceil(inputQuantity / minSaleUnits) * minSaleUnits
      }
    }

    // Apply max quantity constraint
    if (maxQuantity !== Infinity && finalQuantity > maxQuantity) {
      if (addPerBox) {
        // Round down to complete boxes
        finalQuantity = Math.floor(maxQuantity / qtyPerBox) * qtyPerBox
      } else {
        const minSaleUnits = variant.minSaleUnits || 1
        finalQuantity = Math.floor(maxQuantity / minSaleUnits) * minSaleUnits
      }

      if (finalQuantity <= 0) finalQuantity = 0
    }

    // Validate the final quantity
    const validationResult = canAddToCart({
      variant,
      cartLines,
      currentQuantity: 0,
      quantityToAdd: finalQuantity,
    })

    if (!validationResult.canAdd && finalQuantity > 0) finalQuantity = quantity

    // Update cart if quantity changed
    if (finalQuantity !== quantity) updateCartQuantity(variant, finalQuantity)
  }

  return (
    <Div $horizontal={horizontal} $frozen={frozen} $isLoading={isLoading} initial={{ opacity: 0, scaleY: 1 }} animate={{ opacity: 1, scaleY: 1 }} exit={{ opacity: 0 }} transition={{ duration: .2 }}>
      <StyledInput
        type='text'
        inputMode='numeric'
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        $frozen={frozen}
        $isEditing={isEditing}
        $horizontal={horizontal}
        disabled={false}
        aria-label='Cantidad del producto'
      />
    </Div>
  )
})

const Div = styled(motion.div)<{ $horizontal?: boolean, $frozen: boolean, $isLoading?: boolean }>`
  align-items: ${({ $horizontal }) => ($horizontal ? 'center' : 'flex-start')};
  background-color:  ${({ $frozen }) => $frozen ? 'var(--color-card-border-hover--frozen)': 'var(--color-primary-opacity)'};
  border-radius: ${vw(22, 'mobile')};
  bottom: ${({ $horizontal }) => ($horizontal ? 'unset' : '0px')};
  cursor: ${({ $isLoading }) => ($isLoading ? 'wait' : 'auto')};
  display: flex;
  height: ${({ $horizontal }) => $horizontal ? vw(44, 'mobile') : vw(88, 'mobile')};
  justify-content: center;
  max-width: 80px;
  min-height: vw(44, 'mobile');
  overflow: hidden;
  padding: ${({ $horizontal }) => $horizontal ? '0 8px 0' : `${vw(12, 'mobile')} ${vw(3, 'mobile')}`};
  pointer-events: ${({ $isLoading }) => $isLoading ? 'none' : 'auto'};
  position: ${({ $horizontal }) => ($horizontal ? 'relative' : 'absolute')};
  right: ${({ $horizontal }) => ($horizontal ? 'unset' : '0')};
  text-align: center;
  width: ${({ $horizontal }) => $horizontal ? 'fit-content' : vw(44, 'mobile')};

  ${mq.greaterThan('nexus7')} {
    border-radius: ${vw(22, 'tablet')};
    height: ${({ $horizontal }) => $horizontal ? vw(44, 'tablet') : vw(88, 'tablet')};
    min-height: vw(44, 'tablet');
    width: ${({ $horizontal }) => $horizontal ? 'fit-content' : vw(44, 'tablet')};
    padding: ${({ $horizontal }) => $horizontal ? '0 10px 0' : `${vw(10, 'tablet')} ${vw(3, 'tablet')}`};
  }

  ${mq.greaterThan('tablet')} {
    border-radius: ${vw(22, 'desktop')};
    height: ${({ $horizontal }) => vw($horizontal ? 44 : 88, 'desktop')};
    min-height: ${vw(44, 'desktop')};
    width: ${({ $horizontal }) => $horizontal ? 'fit-content' : vw(44, 'desktop')};
    padding: ${({ $horizontal }) => $horizontal ? '0 15px 0' : `${vw(12, 'desktop')} ${vw(3, 'desktop')}`};
  }

  ${mq.greaterThan('desktop')} {
    border-radius: 22px;
    height: ${({ $horizontal }) => ($horizontal ? '44px' : '88px')};
    min-height: 44px;
    width: ${({ $horizontal }) => ($horizontal ? 'fit-content' : '44px')};
    padding: ${({ $horizontal }) => ($horizontal ? '0 30px 0' : '15px 3px')};
  }
`

const StyledInput = styled(motion.input)<{ $frozen: boolean, $isEditing: boolean, $horizontal?: boolean }>`
  background: transparent;
  border: none;
  color: var(--color-white);
  cursor: pointer;
  font-family: var(--font-graphik);
  font-size: 16px;
  font-weight: 500;
  outline: none;
  text-align: center;
  width: fit-content;
  animation: slideUp 0.3s ease-out;

  ${mq.greaterThan('desktop-xs')} {
    font-size: 14px;
  }

  &:focus {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    cursor: text;
  }

  &::placeholder {
    color: rgba(255, 255, 255, .6);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type='number'] {
    -moz-appearance: textfield;
    max-width: 90px;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`
